import React, { useEffect, useMemo, useState } from 'react';
import { Card, Tabs, Table, Button, Space, Tag, Select, Modal, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { useAuthStore } from '@/store/authStore';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import assetService from '@/services/asset.service';
import assetInsuranceService, {
  type InsurancePolicyDto,
  type InsuranceClaimDto,
} from '@/services/asset-insurance.service';
import {
  buildAssetIdToLabelMap,
  formatAssetOption,
  resolveAssetDisplayFromMap,
} from '@/utils/assetDisplay';

const CLAIM_COLOR: Record<string, string> = {
  open: 'blue',
  submitted: 'cyan',
  in_review: 'processing',
  paid: 'success',
  rejected: 'red',
  closed: 'default',
};

const AssetInsurancePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canEdit = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.insurance.edit'));
  const canView = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.insurance.view') ||
      user?.permissions?.includes('asset.view')
  );
  const assetFromUrl = sp.get('asset') || undefined;
  const tabFromUrl = sp.get('tab') || 'policies';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [filterAsset, setFilterAsset] = useState<string | undefined>(assetFromUrl);

  useEffect(() => {
    if (assetFromUrl) setFilterAsset(assetFromUrl);
  }, [assetFromUrl]);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const { data: assets = [] } = useQuery({
    queryKey: ['assets', 'pick'],
    queryFn: () => assetService.list(),
    enabled: canView,
  });
  const assetLabels = useMemo(() => buildAssetIdToLabelMap(assets), [assets]);

  const { data: policies = [], isLoading: lp } = useQuery({
    queryKey: ['asset-insurance', 'policies', filterAsset],
    queryFn: () => assetInsuranceService.listPolicies({ asset_id: filterAsset }),
    enabled: canView,
  });
  const { data: claims = [], isLoading: lc } = useQuery({
    queryKey: ['asset-insurance', 'claims', filterAsset],
    queryFn: () => assetInsuranceService.listClaims({ asset_id: filterAsset }),
    enabled: canView,
  });

  const inv = () => void queryClient.invalidateQueries({ queryKey: ['asset-insurance'] });

  const delPolMut = useMutation({
    mutationFn: (id: string) => assetInsuranceService.removePolicy(id),
    onSuccess: () => {
      message.success('Removed');
      inv();
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });
  const delClMut = useMutation({
    mutationFn: (id: string) => assetInsuranceService.removeClaim(id),
    onSuccess: () => {
      message.success('Removed');
      inv();
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });

  const onFilterAsset = (v: string | null) => {
    const next = v && v.length > 0 ? v : undefined;
    setFilterAsset(next);
    setSp((prev) => {
      if (next) prev.set('asset', next);
      else prev.delete('asset');
      return prev;
    });
  };

  const onTabChange = (key: string) => {
    setActiveTab(key);
    setSp((prev) => {
      prev.set('tab', key);
      return prev;
    });
  };

  const assetQs = filterAsset ? `?asset=${filterAsset}` : '';

  const polCols: ColumnsType<InsurancePolicyDto> = useMemo(() => {
    const base: ColumnsType<InsurancePolicyDto> = [
      { title: 'Policy #', dataIndex: 'policy_number', key: 'p', width: 130 },
      {
        title: 'Asset',
        key: 'a',
        render: (_: unknown, r) => resolveAssetDisplayFromMap(assetLabels, r.asset_id),
      },
      {
        title: 'Insurer',
        dataIndex: 'insurer_name',
        key: 'i',
        ellipsis: true,
        render: (s: string | null) => s || '—',
      },
      {
        title: 'End',
        dataIndex: 'end_date',
        key: 'e',
        width: 110,
        render: (d: string | null) => (d ? d.slice(0, 10) : '—'),
      },
      {
        title: 'Active',
        dataIndex: 'is_active',
        key: 'c',
        width: 70,
        render: (a: boolean) => (a ? 'Yes' : 'No'),
      },
    ];
    if (canEdit) {
      base.push({
        title: 'Actions',
        key: 'x',
        width: 180,
        render: (_: unknown, r) => (
          <Space>
            <Button size="small" onClick={() => navigate(`/asset/insurance/policies/${r.id}`)}>
              Edit
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                void Modal.confirm({
                  title: 'Remove this policy record?',
                  onOk: () => delPolMut.mutateAsync(r.id),
                });
              }}
            >
              Delete
            </Button>
          </Space>
        ),
      });
    }
    return base;
  }, [assetLabels, canEdit, delPolMut, navigate]);

  const clCols: ColumnsType<InsuranceClaimDto> = useMemo(() => {
    const base: ColumnsType<InsuranceClaimDto> = [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 's',
        width: 100,
        render: (s: string) => <Tag color={CLAIM_COLOR[s] || 'default'}>{s?.replace(/_/g, ' ')}</Tag>,
      },
      {
        title: 'Asset',
        key: 'a',
        render: (_: unknown, r) => resolveAssetDisplayFromMap(assetLabels, r.asset_id),
      },
      {
        title: 'Amount',
        dataIndex: 'claim_amount',
        key: 'm',
        width: 90,
        render: (n: number | null) => (n != null ? Number(n).toFixed(0) : '—'),
      },
      {
        title: 'Paid',
        dataIndex: 'amount_paid',
        key: 'p',
        width: 80,
        render: (n: number | null) => (n != null ? Number(n).toFixed(0) : '—'),
      },
    ];
    if (canEdit) {
      base.push({
        title: 'Actions',
        key: 'x',
        width: 180,
        render: (_: unknown, r) => (
          <Space>
            <Button size="small" onClick={() => navigate(`/asset/insurance/claims/${r.id}`)}>
              Edit
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                void Modal.confirm({
                  title: 'Remove this claim?',
                  onOk: () => delClMut.mutateAsync(r.id),
                });
              }}
            >
              Delete
            </Button>
          </Space>
        ),
      });
    }
    return base;
  }, [assetLabels, canEdit, delClMut, navigate]);

  if (!canView) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Asset insurance" documentNumber="" />}>
        <Card>Access denied. Need asset.view or asset.insurance.view</Card>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Asset insurance"
          documentNumber=""
          primaryActions={<Button onClick={() => navigate('/asset/assets')}>Back to assets</Button>}
        />
      }
    >
      <Space wrap style={{ marginBottom: 16 }} align="center">
        <span>Filter by asset</span>
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="All assets"
          value={filterAsset}
          onChange={onFilterAsset as (v: string | null) => void}
          options={assets.map((a) => ({ value: a.id, label: formatAssetOption(a) }))}
          style={{ minWidth: 280 }}
        />
      </Space>
      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={[
          {
            key: 'policies',
            label: 'Policies',
            children: (
              <div>
                {canEdit && (
                  <div style={{ marginBottom: 12 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => navigate(`/asset/insurance/policies/new${assetQs}`)}
                    >
                      New policy
                    </Button>
                  </div>
                )}
                <Table<InsurancePolicyDto>
                  rowKey="id"
                  size="small"
                  loading={lp}
                  dataSource={policies}
                  columns={polCols}
                  pagination={false}
                />
              </div>
            ),
          },
          {
            key: 'claims',
            label: 'Claims',
            children: (
              <div>
                {canEdit && (
                  <div style={{ marginBottom: 12 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => navigate(`/asset/insurance/claims/new${assetQs}`)}
                    >
                      New claim
                    </Button>
                  </div>
                )}
                <Table<InsuranceClaimDto>
                  rowKey="id"
                  size="small"
                  loading={lc}
                  dataSource={claims}
                  columns={clCols}
                  pagination={false}
                />
              </div>
            ),
          },
        ]}
      />
    </WorkspaceLayout>
  );
};

export default AssetInsurancePage;
